import { useState, useEffect, useContext } from 'react';
import { useNavigate } from "react-router-dom";
import AuthContext from '../contexts/auth-context';
import { getQueuesAPI } from '../util';

const LocationsController = ({ children }) => {
  const navigate = useNavigate();
  const authCtx = useContext(AuthContext);
  
  // Loading state
  const [isLoading, setIsLoading] = useState(true);
  
  // Data states - separate offices and events
  const [offices, setOffices] = useState([]);
  const [originalOffices, setOriginalOffices] = useState([]);
  const [officeLocationData, setOfficeLocationData] = useState({});
  
  const [events, setEvents] = useState([]);
  const [originalEvents, setOriginalEvents] = useState([]);
  const [eventLocationData, setEventLocationData] = useState({});
  
  // Search and filter states
  const [currentSearchTerm, setCurrentSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState('');
  
  // Section visibility
  const [showOfficeSection, setShowOfficeSection] = useState(true);
  const [showEventSection, setShowEventSection] = useState(true);

  useEffect(() => {
    // Only fetch data if logged in
    if (authCtx.loggedIn) {
      fetchData();
    } else {
      navigate('/auth');
    }
  }, []);

  useEffect(() => {
    setShowOfficeSection(offices.length > 0);
  }, [offices]);

  useEffect(() => {
    setShowEventSection(events.length > 0);
  }, [events]);

  // Fetch office location data (8am-8pm window)
  const fetchOfficeLocationData = (location) => {
    return new Promise((resolve) => {
      const currentDate = new Intl.DateTimeFormat('en-CA', { 
        timeZone: location.timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).format(new Date());

      getQueuesAPI("get-enterprise-office-current-data-dashboard",
        {
          timezone: location.timeZone,
          org: authCtx.org,
          locationId: location.id,
          measurement: location.measurement,
          doorMeasurement: location.doorMeasurement,
          doorCountSensorIds: location.doorCounterSensorIds,
          doorCounterSensorInfo: location.doorCounterSensorInfo,
          date: currentDate
        },
        (data, error) => {
          if (error) {
            if (error.status === 401) {
              authCtx.logout();
              navigate("/auth");
              resolve({ error: "unauthorized" });
            } else {
              resolve({ error: "failed" });
            }
          } else if (data) {
            resolve({
              locationId: location.id,
              type: 'office',
              data: {
                totalCount: data.totalCount || 0,
                totalCountOut: data.totalCountOut || 0,
                mostRecentTimestamp: data.mostRecentTimestamp || null,
                sensors: data.sensors || []
              }
            });
          } else {
            resolve({ error: "invalid" });
          }
        },
        authCtx.sessionToken
      );
    });
  };

  // Fetch event location data (midnight-11:59pm window)
  // There is an error on the backend with this route because we start at midnight
  // 00:00 is not valid when comparing tz and converting to UTC
  // However we are not using this route for now (2025-11-06)
  // because Events do not need current-data as they are temp and only last a couple days
  // leaving this here for now just incase
  const fetchEventLocationData = (location) => {
    return new Promise((resolve) => {
      // Resolve promise instantly with no data
      resolve({
        locationId: location.id,
        type: 'event',
        data: {
          totalCount: 0,
          totalCountOut: 0,
          mostRecentTimestamp: null,
          sensors: []
        }
      });
      const currentDate = new Intl.DateTimeFormat('en-CA', { 
        timeZone: location.timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).format(new Date());
      // getQueuesAPI("get-enterprise-event-current-data-dashboard",
      //   {
      //     timezone: location.timeZone,
      //     org: authCtx.org,
      //     locationId: location.id,
      //     measurement: location.measurement,
      //     doorMeasurement: location.doorMeasurement,
      //     doorCountSensorIds: location.doorCounterSensorIds,
      //     doorCounterSensorInfo: location.doorCounterSensorInfo,
      //     date: currentDate
      //   },
      //   (data, error) => {
      //     if (error) {
      //       if (error.status === 401) {
      //         authCtx.logout();
      //         navigate("/auth");
      //         resolve({ error: "unauthorized" });
      //       } else {
      //         resolve({ error: "failed" });
      //       }
      //     } else if (data) {
      //       resolve({
      //         locationId: location.id,
      //         type: 'event',
      //         data: {
      //           totalCount: data.totalCount || 0,
      //           totalCountOut: data.totalCountOut || 0,
      //           mostRecentTimestamp: data.mostRecentTimestamp || null,
      //           sensors: data.sensors || []
      //         }
      //       });
      //     } else {
      //       resolve({ error: "invalid" });
      //     }
      //   },
      //   authCtx.sessionToken
      // );
    });
  };

  // Main data fetching function
  const fetchData = async () => {
    setIsLoading(true);

    try {
      // Refresh user auth data first
      const refreshResponse = await new Promise((resolve, reject) => {
        getQueuesAPI(
          "refresh-user-auth-data-enterprise", 
          { org: authCtx.org },
          (data, error) => {
            if (error) {
              reject(error);
            } else {
              resolve(data);
            }
          },
          authCtx.sessionToken
        );
      });

      // Update localStorage and context with new auth data
      if (refreshResponse) {
        console.log('User data refreshed...');
        authCtx.updateAuthLocations(refreshResponse.authLocations);
        authCtx.updateAuthLocationData(refreshResponse.authLocationData);
        authCtx.updateDashboardEnabled(refreshResponse.dashboardEnabled);
      }

      // Get location data from refreshed auth data
      const locationData = authCtx.authLocationData;
      const officeLocations = locationData.offices || [];
      const eventLocations = locationData.events || [];
      
      setOffices(officeLocations);
      setOriginalOffices(officeLocations);
      setEvents(eventLocations);
      setOriginalEvents(eventLocations);

      // Fetch live data for each location type
      const officePromises = officeLocations.map(location => fetchOfficeLocationData(location));
      const eventPromises = eventLocations.map(location => fetchEventLocationData(location));
      
      // Combine all promises
      const allPromises = [...officePromises, ...eventPromises];
      const results = await Promise.all(allPromises);

      const newOfficeData = {};
      const newEventData = {};

      results.forEach(result => {
        if (result.error) {
          console.error(`Error fetching data for location ${result.locationId}:`, result.error);
          return;
        }

        if (result.type === 'office') {
          newOfficeData[result.locationId] = result.data;
        } else if (result.type === 'event') {
          newEventData[result.locationId] = result.data;
        }
      });

      setOfficeLocationData(newOfficeData);
      setEventLocationData(newEventData);
    } catch (error) {
      // console.error("Error fetching location data:", error);
      if (error && error.status === 401) {
        authCtx.logout();
        navigate("/auth");
      } else {
        alert("An error occurred while fetching data. Please refresh the page and try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Search handler
  const handleSearch = (value) => {
    setCurrentSearchTerm(value);
    const searchTerm = value.toLowerCase();

    const filteredOffices = originalOffices.filter(location =>
      location.name?.toLowerCase().includes(searchTerm)
    );
    
    const filteredEvents = originalEvents.filter(location =>
      location.name?.toLowerCase().includes(searchTerm)
    );

    setOffices(filteredOffices);
    setEvents(filteredEvents);
  };

  // Sort handler
  const handleSort = (option, direction) => {
    const sortValue = `${option}-${direction}`;
    setSortConfig(sortValue);

    const sortLocations = (locations, locationData) => {
      return [...locations].sort((a, b) => {
        switch (option) {
          case 'name':
            const nameCompare = a.name.localeCompare(b.name);
            return direction === "asc" ? nameCompare : -nameCompare;
          case 'count':
            const dataA = locationData[a.id];
            const dataB = locationData[b.id];
            const valueA = dataA?.totalCount || 0;
            const valueB = dataB?.totalCount || 0;
            const compareValue = valueA - valueB;
            return direction === "asc" ? compareValue : -compareValue;
          default:
            return 0;
        }
      });
    };

    setOffices(sortLocations(offices, officeLocationData));
    setEvents(sortLocations(events, eventLocationData));
  };

  const refreshLocations = () => {
    fetchData();
  };

  // Prepare props to pass to children
  const controllerProps = {
    // Data
    isLoading,
    offices,
    events,
    officeLocationData,
    eventLocationData,
    
    // Section visibility
    showOfficeSection,
    showEventSection,
    
    // Auth context
    authCtx,
    
    // Handler functions
    handleSearch,
    handleSort,
    refreshLocations,
  };

  return children(controllerProps);
};

export default LocationsController;